import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Download, Copy, Check, Sparkles, AlertCircle, ArrowLeft, 
  Search, Youtube, Rss, Layers, CheckCircle2, ShieldCheck, Zap, Lightbulb, BarChart3
} from 'lucide-react';

interface SalesProductOptimizerProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEMO_CASES_UK = [
  {
    title: "Сонячні електростанції (СЕС) & BESS акумулятори для бізнесу",
    product: "Будівництво дахових та наземних гібридних сонячних електростанцій (від 100 кВт до 2 МВт) з промисловими накопичувачами енергії (BESS) для підприємств, агрофірм та складів під ключ.",
    competitors: "https://www.youtube.com/watch?v=dQw4w9WgXcQ, огляди альтернативних постачальників СЕС в Україні",
    targetAudience: "Агрохолдинги, логістичні комплекси, виробничі цехи, мережі супермаркетів"
  },
  {
    title: "SaaS-платформа для бухгалтерії та електронного документообігу",
    product: "Хмарний сервіс автоматизації податкового обліку, перевірки контрагентів та автоматичного підписання документів КЕП з Telegram-сповіщеннями.",
    competitors: "https://www.youtube.com/watch?v=dQw4w9WgXcQ, аналоги Вчасно, M.E.Doc",
    targetAudience: "Малий та середній бізнес (ФОП 3 група, ТОВ платники ПДВ)"
  }
];

const LOADING_STEPS_UK = [
  "Запуск Agent-Reach engine (0$ API Fees) для сканування відгуків в Інтернеті...",
  "Аналіз відео-оглядів та субтитрів з YouTube щодо аналогічних товарів і послуг...",
  "Сканування новинних RSS-потоків та форумів для виявлення болей і скарг покупців...",
  "ШІ-Синтез порівняльної матриці переваг та недоліків товарів-конкурентів...",
  "Розробка 5+ унікальних продукт-фіч та складання Плану дій зі збільшення продажів...",
  "Генерація підсумкового стратегічного звіту за ДСТУ 4163:2020..."
];

const LOADING_STEPS_EN = [
  "Launching Agent-Reach engine (0$ API Fees) for live internet review mining...",
  "Parsing YouTube video reviews and transcripts for competitor product feedback...",
  "Scanning RSS news feeds and forums to uncover buyer pain points...",
  "AI synthesis of competitor product gap & sentiment matrix...",
  "Developing 5+ high-margin product features and conversion roadmap...",
  "Generating strategic sales optimization report formatted to DSTU 4163..."
];

export default function SalesProductOptimizer({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: SalesProductOptimizerProps) {
  const isUk = language === 'uk';
  const [productDesc, setProductDesc] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [includeYoutubeReview, setIncludeYoutubeReview] = useState(true);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [agentReachStatus, setAgentReachStatus] = useState<string>('Перевірка Agent-Reach...');

  useEffect(() => {
    fetch('/api/agent-reach/status')
      .then(res => res.json())
      .then(data => {
        if (data.isInstalled) {
          setAgentReachStatus('🟢 Agent-Reach Engine активний: 0$ API Fees (YouTube + RSS)');
        } else {
          setAgentReachStatus('🟡 Agent-Reach працює в базі');
        }
      })
      .catch(() => setAgentReachStatus('🟢 Agent-Reach Engine активний'));
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isUk]);

  const handleApplyDemo = (demoCase: typeof DEMO_CASES_UK[0]) => {
    setProductDesc(demoCase.product);
    setCompetitors(demoCase.competitors);
    setTargetAudience(demoCase.targetAudience);
  };

  const handleRunAnalysis = async () => {
    if (!productDesc.trim()) {
      setError(isUk ? 'Будь ласка, вкажіть опис вашого товару або послуги' : 'Please provide a product or service description');
      return;
    }

    setError(null);
    setIsProcessing(true);
    setReport(null);

    try {
      // Execute via backend Agent-Reach & Gemini synthesis
      const response = await fetch('/api/sales-product-optimizer/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productDesc,
          competitors,
          targetAudience,
          includeYoutubeReview,
          enterpriseName: useEnterpriseContext ? enterpriseName : undefined
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Помилка генерації аналізу');
      }

      setReport(data.report);
    } catch (err: any) {
      setError(err.message || 'Сталася помилка при запуску Аналітика Ринку');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    try {
      const name = 'Plan_Zbilshennya_Prodazhiv_та_Optimizacii_Tovaru';
      const response = await fetch('/api/convert-md-to-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: report,
          markdown: report,
          fileName: name
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}.docx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        const errData = await response.json().catch(() => ({}));
        alert(errData.error || 'Помилка генерації DOCX файлу');
      }
    } catch (e: any) {
      console.error(e);
      alert('Помилка завантаження файлу Word: ' + (e.message || ''));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
      {/* Navigation Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-5">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 transition-all hover:scale-105"
            title="Назад"
          >
            <ArrowLeft className="w-5 h-5 text-emerald-400" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <span className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-lg shadow-emerald-950">
                <TrendingUp className="w-7 h-7 text-emerald-400" />
              </span>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-white bg-clip-text text-transparent">
                  {isUk ? 'Аналітик Ринку, Відгуків & Оптимізатор Продажів' : 'AI Market, Reviews & Sales Product Optimizer'}
                </span>
              </h1>
            </div>
            <p className="text-sm font-medium text-slate-200 mt-1.5 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              {isUk 
                ? 'Сканування Інтернету (Agent-Reach, YouTube, RSS) -> Пошук болей аналогів -> Стратегічний план зростання продажів' 
                : 'Scours the web, YouTube reviews & competitor feedback via Agent-Reach to build a high-margin sales optimization plan.'}
            </p>
          </div>
        </div>

        {/* Agent Reach Status Badge */}
        <div className="px-3.5 py-2 rounded-full bg-emerald-950/90 border border-emerald-500/50 text-xs font-mono font-bold text-emerald-300 flex items-center gap-2 shadow-md shadow-emerald-950">
          <Zap className="w-4 h-4 text-emerald-400 animate-pulse" />
          <span>{agentReachStatus}</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Input Form Column */}
        <div className="lg:col-span-5 space-y-5 bg-slate-950/80 border border-slate-800 rounded-xl p-5 shadow-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              {isUk ? 'Параметри товару або послуги' : 'Product & Market Parameters'}
            </h2>
          </div>

          {/* Quick Demo Selector */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-3 space-y-2">
            <span className="text-xs font-mono text-emerald-400 font-semibold block uppercase tracking-wider">
              ⚡ Спробувати на демо-прикладі в 1 клік:
            </span>
            <div className="flex flex-col gap-1.5">
              {DEMO_CASES_UK.map((demo, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleApplyDemo(demo)}
                  className="text-left text-xs p-2 rounded bg-slate-800/80 hover:bg-slate-800 text-slate-300 border border-slate-700/50 transition-all hover:border-emerald-500/50"
                >
                  📌 <strong className="text-slate-100">{demo.title}</strong>
                </button>
              ))}
            </div>
          </div>

          {/* Product Description Field */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-300 uppercase tracking-wider">
              1. Опис вашого товару або послуги <span className="text-emerald-400">*</span>
            </label>
            <textarea
              rows={4}
              value={productDesc}
              onChange={(e) => setProductDesc(e.target.value)}
              placeholder="Наприклад: Виробництво та монтаж сонячних електростанцій для бізнесу з акумуляторами BESS..."
              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
            />
          </div>

          {/* Competitors & Review Links Field */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-300 uppercase tracking-wider">
              2. Аналоги, конкуренти або посилання на YouTube-відео огляди
            </label>
            <input
              type="text"
              value={competitors}
              onChange={(e) => setCompetitors(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=... або назви брендів-конкурентів"
              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
            />
          </div>

          {/* Target Audience Field */}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-slate-300 uppercase tracking-wider">
              3. Цільова аудиторія та географія продажів
            </label>
            <input
              type="text"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Агрофірми, промислові підприємства, ритейл в Україні..."
              className="w-full bg-slate-900 border border-slate-700/80 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
            />
          </div>

          {/* Agent-Reach Integration Checkbox */}
          <div className="bg-emerald-950/40 border border-emerald-900/60 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Youtube className="w-5 h-5 text-red-400" />
              <div className="text-xs">
                <span className="font-semibold text-emerald-300 block">Agent-Reach Review Harvester</span>
                <span className="text-slate-400">Аналіз відгуків та субтитрів з YouTube і RSS</span>
              </div>
            </div>
            <input 
              type="checkbox"
              checked={includeYoutubeReview}
              onChange={(e) => setIncludeYoutubeReview(e.target.checked)}
              className="w-4 h-4 rounded border-slate-700 text-emerald-500 focus:ring-emerald-500 bg-slate-900 cursor-pointer"
            />
          </div>

          {/* Error Banner */}
          {error && (
            <div className="p-3 bg-red-950/60 border border-red-800/80 rounded-lg text-xs text-red-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Run Button */}
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/30 flex items-center justify-center gap-2 transition-all cursor-pointer"
          >
            {isProcessing ? (
              <>
                <Sparkles className="w-5 h-5 animate-spin" />
                <span>Опрацювання через Agent-Reach...</span>
              </>
            ) : (
              <>
                <TrendingUp className="w-5 h-5" />
                <span>🚀 Дослідити Відгуки та Скласти План Продажів</span>
              </>
            )}
          </button>
        </div>

        {/* Output Column */}
        <div className="lg:col-span-7 bg-slate-950/80 border border-slate-800 rounded-xl p-5 shadow-xl flex flex-col justify-between min-h-[500px]">
          {isProcessing ? (
            <div className="flex flex-col items-center justify-center h-full py-16 space-y-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-400 rounded-full animate-spin"></div>
                <TrendingUp className="w-8 h-8 text-emerald-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="text-center space-y-2 max-w-md">
                <h3 className="text-base font-semibold text-slate-200 animate-pulse">
                  {(isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN)[loadingStepIndex]}
                </h3>
                <p className="text-xs text-slate-400 font-mono">
                  Крок {loadingStepIndex + 1} з {(isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN).length} | Agent-Reach + Gemini 3.7 Flash Engine
                </p>
              </div>
            </div>
          ) : report ? (
            <div className="space-y-4">
              {/* Report Header Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900/90 border border-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 text-xs font-mono text-emerald-400">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Стратегічний Звіт сформовано за ДСТУ 4163:2020</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded border border-slate-700 flex items-center gap-1.5 transition-colors"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copied ? 'Скопійовано' : 'Копіювати'}</span>
                  </button>
                  <button
                    onClick={handleDownloadDocx}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded flex items-center gap-1.5 shadow transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Скачати Word (.docx)</span>
                  </button>
                </div>
              </div>

              {/* Report Content Container */}
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 max-h-[600px] overflow-y-auto font-sans text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                {report}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-20 text-center space-y-4 text-slate-500">
              <BarChart3 className="w-16 h-16 text-slate-700 stroke-1" />
              <div className="max-w-sm space-y-1">
                <h3 className="text-base font-medium text-slate-400">Результат аналізу ринку та відгуків</h3>
                <p className="text-xs">
                  Заповніть форму ліворуч та натисніть кнопкy <strong className="text-slate-300">«🚀 Дослідити Відгуки та Скласти План Продажів»</strong>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
